import React, { useState, useMemo } from 'react';
import { useApp } from '@/contexts/AppContext';
import SeverityBadge from '@/components/apex/SeverityBadge';
import RecordDrawer from '@/components/apex/RecordDrawer';
import useListDrawer from '@/hooks/useListDrawer';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Search, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import AnimatedCounter from '@/components/apex/AnimatedCounter';
import HowCalculated from '@/components/apex/HowCalculated';
import ProofStatusBadge from '@/components/apex/ProofStatusBadge';
import ProvenanceDepthBar from '@/components/apex/ProvenanceDepthBar';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import InvestigationBadge from '@/components/apex/InvestigationBadge';

const RISK_RANK = { critical: 0, high: 1, medium: 2, low: 3 };

export default function IdentityProof() {
  const { seed } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState('90d');
  const [q, setQ] = useState('');

  const usedFor = (i) =>
    tab === '30d'  ? i.used_30d :
    tab === '365d' ? i.used_365d :
    tab === 'full' ? i.used_full :
                      i.used_90d;
  const effectiveFor = (i) => {
    const used = usedFor(i);
    return i.granted > 0 ? (used / i.granted) * 100 : 0;
  };

  const filtered = useMemo(() => {
    if (!seed) return [];
    return seed.identities
      .filter((i) => !q || i.principal.toLowerCase().includes(q.toLowerCase()) || i.owner.toLowerCase().includes(q.toLowerCase()))
      .sort((a, b) => (RISK_RANK[a.risk] ?? 9) - (RISK_RANK[b.risk] ?? 9));
  }, [seed, q]);

  const {
    active: sel,
    setActive,
    openPrev,
    openNext,
    index,
    total,
  } = useListDrawer({
    items: filtered,
    idKey: 'principal',
    paramKey: 'open',
    getLabel: (i) => i?.principal,
  });

  if (!seed) return null;

  const buildRecord = (i) => {
    const used = usedFor(i);
    const eff = effectiveFor(i);
    const tone = i.risk === 'critical' ? 'critical' : i.risk === 'high' ? 'warning' : 'neutral';
    return {
      idTag: i.principal,
      title: i.principal,
      severity: i.risk,
      subtitle: `${i.type} · ${i.owner} · Last active: ${i.last_active}`,
      verdict: {
        tone,
        label: eff < 5 ? 'Behavioral access drift' : eff < 30 ? 'Overpermissioned role' : 'Permission footprint acceptable',
        sub: `${i.granted} permissions granted. ${used} used in the selected window. This identity's effective footprint is ${eff.toFixed(1)}% of its granted surface — ${i.granted - used} actions are unguarded attack surface with no behavioral justification.`,
      },
      signal: i.anomaly ? {
        tone: 'critical',
        lead: 'First-seen anomaly:',
        body: `${i.anomaly}${i.anomaly_time ? ' · Detected: ' + i.anomaly_time : ''}. This action has never occurred in 14 months of CloudTrail history for this identity. Deterministic — not a statistical estimate.`,
      } : {
        tone: 'improving',
        lead: 'Behavioral baseline:',
        body: 'No first-seen anomalies detected. This identity is operating within its established behavioral envelope. The opportunity is permission right-sizing — not anomaly response.',
      },
      meta: [
        { label: 'Granted permissions', value: `${i.granted}`, mono: true, sub: 'From all attached policies' },
        { label: `Used (${tab})`, value: `${used}`, mono: true, tone: 'warning', sub: 'Observed in CloudTrail', delta: 'flat' },
        { label: 'Effective footprint', value: `${eff.toFixed(1)}%`, mono: true, tone: eff < 5 ? 'critical' : eff < 30 ? 'warning' : 'success', sub: `${i.granted - used} unused actions`, delta: eff < 30 ? 'down' : 'flat' },
        { label: 'Last policy review', value: i.last_review ?? 'Never reviewed', tone: i.last_review ? '' : 'critical', sub: i.owner },
        { label: 'Behavioral proof status', value: 'PROVEN', tone: 'success', mono: true, sub: `Derived from ${(i.api_calls_analyzed ?? 127847).toLocaleString()} CloudTrail events` },
        { label: 'Provenance depth', value: `${eff < 5 ? '9.2' : eff < 30 ? '8.1' : '6.4'} / 10`, tone: eff < 5 ? 'success' : eff < 30 ? 'lens' : 'warning', mono: true, sub: 'CloudTrail management events · IAM policy simulation' },
        ...(i.investigation ? [{ label: 'Linked investigation', value: i.investigation, mono: true, tone: 'critical', sub: 'Active CISO inquiry · click to open', pivot: { to: `/lens/investigations?id=${i.investigation}`, label: `Open ${i.investigation}` } }] : []),
      ],
      sections: [
        {
          title: 'Permission reality',
          type: 'chart',
          chart: {
            kind: 'stack',
            label: 'Granted vs used vs attack surface',
            sublabel: 'Based on CloudTrail behavioral analysis',
            total: i.granted,
            segments: [
              { label: 'Used', value: used, color: '#0D9488' },
              { label: 'Unused (attack surface)', value: Math.max(0, i.granted - used), color: '#FCA5A5' },
            ],
          },
        },
        {
          title: 'Permission grant history',
          type: 'chart',
          chart: {
            kind: 'area',
            label: 'Granted permissions over time',
            sublabel: "How this role's attack surface grew",
            accent: '#DC2626',
            threshold: used,
            labels: ['14mo ago', '9mo ago', '6mo ago', '3mo ago', 'Today'],
            values: [
              Math.round(i.granted * 0.25),
              Math.round(i.granted * 0.49),
              Math.round(i.granted * 0.72),
              Math.round(i.granted * 0.91),
              i.granted,
            ],
          },
        },
        {
          title: 'Resources accessed in selected window',
          type: 'kv',
          items: i.accessed_resources_structured ?? [
            { k: 's3:::stratum-pci-customer-data', v: 'GetObject, ListBucket · PCI data', tone: 'critical', pivot: { to: '/lens/evidence', label: 'View evidence' } },
            { k: `kms:${i.principal.includes('payments') ? 'payments' : 'default'}-cmk`, v: 'Decrypt · 4 calls', tone: 'warning', pivot: { to: '/lens/identity', label: 'View KMS access' } },
            { k: 'logs:stratum-payments-prod', v: 'PutLogEvents · 847 calls', tone: 'neutral' },
            { k: 'ecs:stratum-payments-api', v: 'DescribeTaskDefinition · 12 calls', tone: 'neutral' },
          ],
        },
        {
          title: 'Similar roles with same pattern',
          type: 'kv',
          items: [
            { k: 'lambda-reporting-processor', v: `${Math.round(i.granted * 0.85)} granted · ${Math.round(used * 0.7)} used`, tone: 'warning', pivot: { to: '/lens/identity?open=lambda-reporting-processor', label: 'Inspect this role' } },
            { k: 'ecs-task-executor-prod',     v: `${Math.round(i.granted * 1.1)} granted · ${Math.round(used * 1.2)} used`, tone: 'warning', pivot: { to: '/lens/identity?open=ecs-task-executor-prod',     label: 'Inspect this role' } },
            { k: 'data-pipeline-role',         v: `${Math.round(i.granted * 0.6)} granted · ${Math.round(used * 0.5)} used`, tone: 'warning', pivot: { to: '/lens/identity?open=data-pipeline-role',         label: 'Inspect this role' } },
          ],
        },
        {
          title: 'Generated least-privilege policy',
          type: 'code',
          body: `{\n  "Version": "2012-10-17",\n  "Statement": [{\n    "Effect": "Allow",\n    "Action": [\n      "s3:GetObject",\n      "s3:ListBucket",\n      "kms:Decrypt",\n      "logs:PutLogEvents"\n    ],\n    "Resource": [\n      "arn:aws:s3:::stratum-pci-customer-data",\n      "arn:aws:s3:::stratum-pci-customer-data/*"\n    ]\n  }]\n}`,
        },
        {
          title: 'Evidence chain',
          type: 'paragraph',
          mono: true,
          body: `Derived from 14 months of CloudTrail history · ${(i.api_calls_analyzed ?? 127847).toLocaleString()} API calls analyzed · Evidence ID: ${i.evidence_id ?? 'EVD-2026-04-21-' + i.principal} · SHA-256: ${i.evidence_hash ?? 'a3f2b1c8d9e0...'}`,
        },
      ],
      actions: [
        { label: 'Deploy generated policy', primary: true, testId: 'drawer-deploy-policy', onClick: () => toast.success('Generated policy queued for review', { description: i.principal }) },
        { label: 'Open Remediation', testId: 'drawer-open-remediation', onClick: () => navigate('/lens/remediation') },
        ...(i.investigation ? [{ label: `Open ${i.investigation}`, testId: 'drawer-open-inv', onClick: () => navigate(`/lens/investigations?id=${i.investigation}`) }] : []),
      ],
    };
  };

  return (
    <div className="space-y-5" data-testid="identity-proof">
      <div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Evidence Engine · Identity Proof</span>
        <h1 className="text-2xl font-bold tracking-tight mt-1">Permission reality vs grant</h1>
        <p className="text-sm text-apex-neutral mt-1">{seed.identity_summary.tracked} identities tracked · {seed.identity_summary.overpermissioned} overpermissioned · {seed.identity_summary.first_seen_anomalies} first-seen anomalies · {seed.identity_summary.cross_environment_paths} cross-environment paths</p>
      </div>

      {/* Team summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {seed.identity_summary.team_summary.map((t) => (
          <div key={t.team} className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">{t.team}</div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-bold tabular text-apex-critical"><AnimatedCounter value={t.excess_pct} suffix="%" /></span>
              <span className="text-xs text-apex-neutral">excess access</span>
            </div>
            <div className="text-[11px] text-apex-neutral mt-0.5">{t.roles} roles</div>
          </div>
        ))}
      </div>

      {/* How Effective % is calculated */}
      <HowCalculated storageKey="identity-effective" testId="how-calculated-effective" label="How Effective % is calculated">
        <p className="text-apex-ink-2 mb-3">
          Effective % = <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">used ÷ granted × 100</span> over the selected window. We compare the permissions an identity actually invoked against the permissions IAM grants it.
        </p>
        <ul className="space-y-2">
          <li className="flex items-baseline gap-2"><span className="text-apex-lens font-mono text-[11px] shrink-0">USED</span><span><strong className="text-apex-ink">Distinct (action, resource ARN) pairs</strong> where this principal had a successful CloudTrail event in the window. Failed AccessDenied calls are excluded; cross-account assumed sessions are attributed to the source role.</span></li>
          <li className="flex items-baseline gap-2"><span className="text-apex-lens font-mono text-[11px] shrink-0">GRANTED</span><span><strong className="text-apex-ink">Sum of every action × resource</strong> the identity is allowed to invoke per its attached policies, with wildcards expanded against your live resource inventory.</span></li>
          <li className="flex items-baseline gap-2"><span className="text-apex-lens font-mono text-[11px] shrink-0">WINDOW</span><span><strong className="text-apex-ink">30d / 90d / 365d / Full</strong> picker above. "Full" reaches back to the earliest CloudTrail record we hold for the account ({'14 months'}).</span></li>
        </ul>
        <div className="mt-3 pt-3 border-t border-slate-200">
          <div className="text-[10px] uppercase font-mono tracking-wider text-apex-neutral mb-2">Risk thresholds</div>
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <div className="border border-red-200 bg-red-50 rounded px-2 py-1.5">
              <div className="font-mono text-[10px] text-apex-critical">CRITICAL</div>
              <div className="text-apex-ink font-semibold">&lt; 5% effective</div>
              <div className="text-[10px] text-apex-neutral">95%+ permissions never used</div>
            </div>
            <div className="border border-amber-200 bg-amber-50 rounded px-2 py-1.5">
              <div className="font-mono text-[10px] text-amber-700">WARNING</div>
              <div className="text-apex-ink font-semibold">5 – 30%</div>
              <div className="text-[10px] text-apex-neutral">Significant excess</div>
            </div>
            <div className="border border-emerald-200 bg-emerald-50 rounded px-2 py-1.5">
              <div className="font-mono text-[10px] text-emerald-700">HEALTHY</div>
              <div className="text-apex-ink font-semibold">≥ 30%</div>
              <div className="text-[10px] text-apex-neutral">Reasonable utilization</div>
            </div>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-2 gap-2 text-[11px] font-mono">
          <span className="text-apex-neutral">Identities scored</span><span className="text-right">{seed.identities.length}</span>
          <span className="text-apex-neutral">Wildcard expansion</span><span className="text-right">Live resource inventory</span>
          <span className="text-apex-neutral">Last computed</span><span className="text-right">7 minutes ago</span>
          <span className="text-apex-neutral">Confidence</span><span className="text-right text-apex-lens">High</span>
        </div>
      </HowCalculated>

      {/* Filter bar */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input data-testid="identity-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search principal, owner…" className="pl-9 h-9" />
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="30d">30d</TabsTrigger>
            <TabsTrigger value="90d">90d</TabsTrigger>
            <TabsTrigger value="365d">365d</TabsTrigger>
            <TabsTrigger value="full">Full</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Identity list */}
      <div className="hidden md:block bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="identity-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-apex-neutral">
            <tr>
              <Th>Principal</Th>
              <Th>Type</Th>
              <Th className="text-right">Granted</Th>
              <Th className="text-right" data-testid="col-used">Used ({tab})</Th>
              <Th className="text-right">Proof</Th>
              <Th className="text-right">Effective %</Th>
              <Th className="hidden lg:table-cell">Anomaly</Th>
              <Th>Risk</Th>
              <Th className="text-right">Action</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((i, idx) => (
              <motion.tr
                key={i.principal}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.03 }}
                data-testid={`identity-${i.principal}`}
                onClick={() => setActive(i)}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(i); } }}
                className={`hover:bg-slate-50 cursor-pointer ${i.investigation === 'INV-2026-042' ? 'bg-amber-50/40' : ''}`}
              >
                <Td>
                  <span className="font-mono text-[12px]">{i.principal}</span>
                  {i.principal === 'lambda-payments-processor' && (
                    <span className="inline-block align-middle ml-1.5">
                      <InvestigationBadge />
                    </span>
                  )}
                </Td>
                <Td><span className="text-xs">{i.type}</span></Td>
                <Td className="text-right tabular text-xs">{i.granted}</Td>
                <Td className="text-right tabular text-xs" data-testid={`identity-${i.principal}-used`}>{usedFor(i)}</Td>
                <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex items-center justify-end" data-testid={`identity-${i.principal}-proof`}>
                    <ProofStatusBadge status={i.used_90d < 20 ? 'PROVEN' : 'INFERRED'} size="xs" showLabel={false} />
                  </div>
                </Td>
                <Td className="text-right">
                  {(() => {
                    const eff = effectiveFor(i);
                    return (
                      <span className={`tabular text-xs font-semibold ${eff < 5 ? 'text-apex-critical' : eff < 30 ? 'text-apex-warning' : 'text-apex-success'}`}>
                        {eff.toFixed(1)}%
                      </span>
                    );
                  })()}
                </Td>
                <Td className="hidden lg:table-cell">
                  {i.anomaly ? <span className="text-[11px] text-apex-warning">{i.anomaly}</span> : <span className="text-[11px] text-slate-400">None</span>}
                </Td>
                <Td><SeverityBadge severity={i.risk} size="sm" /></Td>
                <Td className="text-right">
                  <button onClick={(e) => { e.stopPropagation(); setActive(i); }} data-testid={`identity-${i.principal}-view`} className="text-xs font-semibold text-apex-lens inline-flex items-center gap-1">
                    View proof <ArrowRight className="w-3 h-3" />
                  </button>
                </Td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {filtered.map((i) => (
          <button
            key={i.principal}
            onClick={() => setActive(i)}
            className="w-full text-left bg-white rounded-lg border border-slate-200 p-4 hover:border-apex-lens transition-colors"
            data-testid={`identity-card-${i.principal}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <span className="font-mono text-[12px] truncate">
                {i.principal}
                {i.principal === 'lambda-payments-processor' && (
                  <span className="inline-block align-middle ml-1.5">
                    <InvestigationBadge />
                  </span>
                )}
              </span>
              <SeverityBadge severity={i.risk} size="sm" />
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px] mt-2">
              <Stat label="Granted" value={i.granted} />
              <Stat label={`Used (${tab})`} value={usedFor(i)} />
              <Stat label="Effective" value={`${effectiveFor(i).toFixed(1)}%`} />
            </div>
            {i.anomaly && <div className="mt-2 text-[11px] text-apex-warning">⚠ {i.anomaly}</div>}
          </button>
        ))}
      </div>

      {/* Drawer */}
      <RecordDrawer
        open={!!sel}
        onOpenChange={(o) => !o && setActive(null)}
        record={sel ? buildRecord(sel) : null}
        testId="identity-drawer"
        onPrev={openPrev}
        onNext={openNext}
        position={index}
        total={total}
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-apex-neutral">{label}</div>
      <div className="font-semibold tabular">{value}</div>
    </div>
  );
}
function Th({ children, className = '', ...rest }) {
  return <th {...rest} className={`px-3 py-2 text-left font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = '', ...rest }) {
  return <td {...rest} className={`px-3 py-2 ${className}`}>{children}</td>;
}
